import { forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-bootstrap-formutil';
import FormLayout from 'app/components/FormLayout';
import Alert from 'components/base/Alert';
import Button from 'components/base/Button';
import Modal, { ModalHandler } from 'components/base/Modal';
import {
    BeneficiaryRelationshipEnum,
    BeneficiaryTypeEnum,
    CustomerInfoKeyType,
    MaritalStatusEnum
} from 'types/customer';
import BeneficiaryItem from './BeneficiaryItem';
import styles from './style.scss';
import { BeneficiaryInfoFormutil } from './type';

interface BeneficiaryFormProps {
    // 开第一个户的时候的需要比对一下, 受益人的税号不能跟账户主人的税号一样, 所以这里要传入账户主人的税号
    masterTaxNumber?: string;
}

export interface BeneficiaryFormRef {
    validateBeneficiary: (params: { homeStateCode: string; maritalStatus: MaritalStatusEnum }) => Promise<boolean>;
}

export const defaultFieldsOfBeneficiary: CustomerInfoKeyType[] = [
    'temporaryPrimaryBeneficiary',
    'temporarySecondaryBeneficiary'
];

const MAX_BENEFICIARY_NUMBER = 5;
const BeneficiaryForm = forwardRef<BeneficiaryFormRef, BeneficiaryFormProps>((props, ref) => {
    const { masterTaxNumber } = props;
    const $formutil = useForm<BeneficiaryInfoFormutil>();
    const { $params, $getField, $states } = $formutil;
    const { temporaryPrimaryBeneficiary, temporarySecondaryBeneficiary } = $params;
    // getField(xxx).$invalid拿到的数据不是实时最新的值,getField().$new()也不行, 所以这里从$states里面取
    const { $invalid: $primaryInvalid, $dirty: $primaryDirty } = $states.temporaryPrimaryBeneficiary || {};
    const { $invalid: $secondaryInvalid, $dirty: $secondaryDirty } = $states.temporarySecondaryBeneficiary || {};

    useImperativeHandle(ref, () => ({
        validateBeneficiary: async ({ homeStateCode, maritalStatus }) => {
            let modalText = '';

            // https://tigertech.feishu.cn/docx/UpoAdQdtkoRSKKxk1UccbkilnEf
            if (['AZ', 'CA', 'ID', 'LA', 'NV', 'NM', 'TX', 'WA', 'WI'].includes(homeStateCode)) {
                if (maritalStatus !== MaritalStatusEnum.MARRIED) {
                    modalText = __(
                        'I understand that if I become married in the future, I must complete a new Change of Beneficiary form.'
                    );
                } else if (
                    // 用户指定了配偶以外的主要受益人
                    temporaryPrimaryBeneficiary?.some(
                        ({ beneficiary_relationship }) =>
                            // 这里判断一下受益人关系是否存在是为了过滤没有该项信息的数据
                            beneficiary_relationship && beneficiary_relationship !== BeneficiaryRelationshipEnum.SPOUSE
                    )
                ) {
                    modalText = __(
                        "You have designated someone other than your spouse to be the Primary Beneficiary for your TradeUP IRA Account. We are required by law to obtain your spouse's signature and consent for your beneficiary designation. We will send an email to your e-mail address."
                    );
                }

                if (modalText) {
                    return Modal.openSlideModal({
                        component: ({ close, dismiss }: ModalHandler) => {
                            return (
                                <div className="pt-2 pb-3">
                                    <h4 className="mt-3 mb-4">{__('Beneficiary Directions')}</h4>
                                    <div className="mb-4">{modalText}</div>
                                    <FormLayout layout="1:2">
                                        <Button type="light" round onClick={() => dismiss()} block>
                                            {__('返回')}
                                        </Button>
                                        <Button round type="primary" onClick={() => close()} block>
                                            {__('继续')}
                                        </Button>
                                    </FormLayout>
                                </div>
                            );
                        },
                        responsive: true,
                        backdrop: 'static'
                    })
                        .result.then(() => {
                            return true;
                        })
                        .catch(() => {
                            return false;
                        });
                }

                return true;
            }

            return true;
        }
    }));

    return (
        <div>
            <div>
                <div className="open-title mt-0">
                    <h3 className={styles.title}>{__('Beneficiary Information')}</h3>
                    <p className={styles.description}>
                        {__(
                            'The following individuals shall be my primary and/or contingent beneficiary(ies) on my TradeUP IRA Account (“Account”). If any primary or contingent beneficiary predeceases you, that beneficiary interest shall terminate completely and the percentage share of any remaining beneficiary(ies) shall be increased on a pro rata basis. If no primary beneficiary(ies) survives you or renounces rights, the contingent beneficiary(ies) shall acquire the designated share of your Account. If no primary or contingent beneficiary survives you or they renounce their rights, your spouse shall acquire the designated shares of your Account. If no primary or contingent beneficiary survives you or they renounce their rights, and then if your spouse does not survive you, renounces rights, or you do not have a spouse at the time of your death, your estate shall acquire the designated shares of your Account.'
                        )}
                    </p>
                </div>
            </div>
            <div className={styles.container}>
                <h5>{__('Primary Beneficiary(ies)*')}</h5>
                <p className={styles.description}>
                    {__('If more than one Primary Beneficiary is listed, percentage is required and must total 100%.')}
                </p>
                {/* 必须要有错误的时候才展示 */}
                {$primaryInvalid && $primaryDirty && (
                    <Alert variant="danger">{$getField('temporaryPrimaryBeneficiary')?.$getFirstError()}</Alert>
                )}
                <BeneficiaryItem
                    name="temporaryPrimaryBeneficiary"
                    type={BeneficiaryTypeEnum.PRIMARY}
                    primaryBeneficiaryInfo={temporaryPrimaryBeneficiary || []}
                    secondaryBeneficiaryInfo={temporarySecondaryBeneficiary || []}
                    maxNumber={MAX_BENEFICIARY_NUMBER}
                    masterTaxNumber={masterTaxNumber}
                />
            </div>
            <div className={styles.container}>
                <h5>{__('Contingent Beneficiary(ies)')}</h5>
                <p className={styles.description}>
                    {__(
                        'If more than one Contingent Beneficiary is listed, percentage is required and must total 100%.'
                    )}
                </p>
                {/* 必须要有错误的时候才展示 */}
                {$secondaryInvalid && $secondaryDirty && (
                    <Alert variant="danger">{$getField('temporarySecondaryBeneficiary')?.$getFirstError()}</Alert>
                )}
                <BeneficiaryItem
                    name="temporarySecondaryBeneficiary"
                    type={BeneficiaryTypeEnum.SECONDARY}
                    primaryBeneficiaryInfo={temporaryPrimaryBeneficiary || []}
                    secondaryBeneficiaryInfo={temporarySecondaryBeneficiary || []}
                    maxNumber={MAX_BENEFICIARY_NUMBER}
                    masterTaxNumber={masterTaxNumber}
                />
            </div>
        </div>
    );
});

export default BeneficiaryForm;
export * from './type';
